import React, { useState } from 'react';
import { fetchUserProfile } from '../../../store/slices/authSlice';
import { useAppDispatch } from '../../../store/hooks';
import {
  Container,
  Title,
  
  Grid,
  Text,
  Badge,
  Group,
  Avatar,
  Stack,
  Button,
  Divider,
  Card,
  ThemeIcon,
 
  Alert,
  TextInput,
  PasswordInput,
  Switch,
  Tabs,
} from '@mantine/core';
import {
  IconMail,
  IconPhone,
  IconCalendar,
  IconClock,
  IconShield,
  IconLock,
  IconLockOpen,
  IconEdit,
  IconDeviceFloppy,
  IconKey,
  IconUser,
} from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import axios from 'axios';
import { useAppSelector } from '../../../store/hooks';

export const ProfilePage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const { user, token } = useAppSelector((state) => state.auth);
  const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';
const dispatch = useAppDispatch();

  const form = useForm({
    initialValues: {
      fullName: user?.fullName || '',
      email: user?.email || '',
      phoneNumber: user?.phoneNumber || '',
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      phoneNumber: (value) => (value.length < 10 ? 'Invalid phone number' : null),
      fullName: (value) => (value.length < 2 ? 'Full name is too short' : null),
    },
  });

  const passwordForm = useForm({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate: {
      currentPassword: (value) => (value.length < 6 ? 'Password must be at least 6 characters' : null),
      newPassword: (value) => (value.length < 6 ? 'Password must be at least 6 characters' : null),
      confirmPassword: (value, values) => 
        value !== values.newPassword ? 'Passwords do not match' : null,
    },
  });

  const handleUpdateProfile = async (values: typeof form.values) => {
  setLoading(true);
  try {
    const payload = {
      username: user?.username,          // ✅ required by backend
      fullName: values.fullName,
      email: values.email,
      phoneNumber: values.phoneNumber,
      enabled: user?.enabled,            // keep existing status
      twoFactorEnabled: user?.twoFactorEnabled,
      roles: user?.roles,                // keep existing roles
    };
    await axios.put(
      `${API_URL}/users/${user?.id}`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    notifications.show({
      title: 'Success',
      message: 'Profile updated successfully',
      color: 'green',
    });
    await dispatch(fetchUserProfile());
    setEditing(false);
    // Optionally refresh user profile in Redux
    // await dispatch(fetchUserProfile());
  } catch (error: any) {
    notifications.show({
      title: 'Error',
      message: error.response?.data?.message || 'Failed to update profile',
      color: 'red',
    });
  } finally {
    setLoading(false);
  }
};

  const handleChangePassword = async (values: typeof passwordForm.values) => {
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/auth/change-password`,
        {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      notifications.show({
        title: 'Success',
        message: 'Password changed successfully',
        color: 'green',
      });
      passwordForm.reset();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to change password',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Container>
        <Alert color="red">User not found</Alert>
      </Container>
    );
  }

  return (
    <Container size="lg">
      <Grid>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Card withBorder padding="xl" radius="md">
            <Stack align="center" gap="md">
              <Avatar size={120} color="exportGreen" radius={120}>
                {user.fullName?.charAt(0).toUpperCase()}
              </Avatar>
              <Title order={3} ta="center">{user.fullName}</Title>
              <Text size="sm" c="dimmed">@{user.username}</Text>
              
              <Group gap="xs">
                {user.roles?.map((role: string) => (
                  <Badge key={role} color="exportGreen" size="lg">
                    {role.replace('ROLE_', '')}
                  </Badge>
                ))}
              </Group>

              <Divider my="sm" />

              <Group gap="xs">
                <ThemeIcon color={user.enabled ? 'green' : 'red'} variant="light">
                  {user.enabled ? <IconLockOpen size={16} /> : <IconLock size={16} />}
                </ThemeIcon>
                <Text size="sm">Status: {user.enabled ? 'Active' : 'Locked'}</Text>
              </Group>

              <Group gap="xs">
                <ThemeIcon color={user.twoFactorEnabled ? 'blue' : 'gray'} variant="light">
                  <IconShield size={16} />
                </ThemeIcon>
                <Text size="sm">2FA: {user.twoFactorEnabled ? 'Enabled' : 'Disabled'}</Text>
              </Group>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 8 }}>
          <Tabs defaultValue="profile">
            <Tabs.List>
              <Tabs.Tab value="profile" leftSection={<IconUser size={16} />}>
                Profile Information
              </Tabs.Tab>
              <Tabs.Tab value="security" leftSection={<IconKey size={16} />}>
                Security
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="profile" pt="xl">
              <Card withBorder padding="xl" radius="md">
                <Group justify="space-between" mb="lg">
                  <Title order={4}>Profile Information</Title>
                  {!editing && (
                    <Button
                      leftSection={<IconEdit size={16} />}
                      variant="light"
                      color="exportGreen"
                      onClick={() => setEditing(true)}
                    >
                      Edit Profile
                    </Button>
                  )}
                </Group>

                {editing ? (
                  <form onSubmit={form.onSubmit(handleUpdateProfile)}>
                    <Stack>
                      <TextInput
                        label="Full Name"
                        {...form.getInputProps('fullName')}
                        required
                      />
                      <TextInput
                        label="Email"
                        {...form.getInputProps('email')}
                        required
                      />
                      <TextInput
                        label="Phone Number"
                        {...form.getInputProps('phoneNumber')}
                        required
                      />
                      <Group justify="flex-end" mt="md">
                        <Button variant="light" onClick={() => setEditing(false)}>
                          Cancel
                        </Button>
                        <Button 
                          type="submit" 
                          color="exportGreen"
                          loading={loading}
                          leftSection={<IconDeviceFloppy size={16} />}
                        >
                          Save Changes
                        </Button>
                      </Group>
                    </Stack>
                  </form>
                ) : (
                  <Stack gap="md">
                    <Group>
                      <ThemeIcon color="blue" variant="light">
                        <IconUser size={16} />
                      </ThemeIcon>
                      <div>
                        <Text size="sm" c="dimmed">Full Name</Text>
                        <Text fw={500}>{user.fullName}</Text>
                      </div>
                    </Group>

                    <Group>
                      <ThemeIcon color="blue" variant="light">
                        <IconMail size={16} />
                      </ThemeIcon>
                      <div>
                        <Text size="sm" c="dimmed">Email</Text>
                        <Text fw={500}>{user.email}</Text>
                        {user.emailVerified && (
                          <Badge color="green" size="xs">Verified</Badge>
                        )}
                      </div>
                    </Group>

                    <Group>
                      <ThemeIcon color="green" variant="light">
                        <IconPhone size={16} />
                      </ThemeIcon>
                      <div>
                        <Text size="sm" c="dimmed">Phone</Text>
                        <Text fw={500}>{user.phoneNumber}</Text>
                        {user.phoneVerified && (
                          <Badge color="green" size="xs">Verified</Badge>
                        )}
                      </div>
                    </Group>

                    <Group>
                      <ThemeIcon color="orange" variant="light">
                        <IconCalendar size={16} />
                      </ThemeIcon>
                      <div>
                        <Text size="sm" c="dimmed">Member Since</Text>
                        <Text fw={500}>
                          {new Date(user.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </Text>
                      </div>
                    </Group>

                    <Group>
                      <ThemeIcon color="cyan" variant="light">
                        <IconClock size={16} />
                      </ThemeIcon>
                      <div>
                        <Text size="sm" c="dimmed">Last Login</Text>
                        <Text fw={500}>
                          {user.lastLoginAt
                            ? new Date(user.lastLoginAt).toLocaleString()
                            : 'Never'
                          }
                        </Text>
                      </div>
                    </Group>
                  </Stack>
                )}
              </Card>
            </Tabs.Panel>

            <Tabs.Panel value="security" pt="xl">
              <Card withBorder padding="xl" radius="md">
                <Title order={4} mb="lg">Change Password</Title>
                
                <form onSubmit={passwordForm.onSubmit(handleChangePassword)}>
                  <Stack>
                    <PasswordInput
                      label="Current Password"
                      placeholder="Enter current password"
                      {...passwordForm.getInputProps('currentPassword')}
                      required
                    />
                    
                    <PasswordInput
                      label="New Password"
                      placeholder="Enter new password"
                      {...passwordForm.getInputProps('newPassword')}
                      required
                    />
                    
                    <PasswordInput
                      label="Confirm New Password"
                      placeholder="Confirm new password"
                      {...passwordForm.getInputProps('confirmPassword')}
                      required
                    />

                    <Group justify="flex-end" mt="md">
                      <Button 
                        type="submit" 
                        color="exportGreen"
                        loading={loading}
                      >
                        Change Password
                      </Button>
                    </Group>
                  </Stack>
                </form>

                <Divider my="xl" />

                <Title order={4} mb="lg">Two-Factor Authentication</Title>
                
                <Group justify="space-between">
                  <div>
                    <Text fw={500}>Two-Factor Authentication</Text>
                    <Text size="sm" c="dimmed">
                      Add an extra layer of security to your account
                    </Text>
                  </div>
                  <Switch
                    checked={user.twoFactorEnabled}
                    onChange={async () => {
                      try {
                        if (user.twoFactorEnabled) {
                          await axios.post(
                            `${API_URL}/auth/disable-2fa`,
                            { userId: user.id },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          notifications.show({
                            title: 'Success',
                            message: '2FA disabled successfully',
                            color: 'blue',
                          });
                        } else {
                          const response = await axios.post(
                            `${API_URL}/auth/setup-2fa?userId=${user.id}`,
                            {},
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          notifications.show({
                            title: 'Success',
                            message: '2FA setup initiated',
                            color: 'blue',
                          });
                        }
                      } catch (error: any) {
                        notifications.show({
                          title: 'Error',
                          message: error.response?.data?.message || 'Failed to update 2FA settings',
                          color: 'red',
                        });
                      }
                    }}
                    label={user.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                  />
                </Group>
              </Card>
            </Tabs.Panel>
          </Tabs>
        </Grid.Col>
      </Grid>
    </Container>
  );
};